import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect} from "react";
import {useParams} from "react-router";

import {GuideStateApi} from "@repo/guide-state";

import ErrorLoadingPage from "#app-shell-package/errors/error-loading-page.tsx";
import PageRoot from "#app-shell-package/page-root.tsx";
import DefaultWithKhanmigoPageTemplate from "#app-shell-package/page-templates/with-khanmigo-templates/default-with-khanmigo-page-template.tsx";
import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import useProfile from "#data-access/use-profile.ts";
import {useUpdatePageSettings} from "#data-access/use-update-page-settings.ts";
import {getTermMap} from "#scratchpads-package/user-authored-content-types.ts";

import {withStrings} from "./locales/new-user-program-page/load-strings.tsx";
import ScratchpadPage from "./scratchpad-page.tsx";
import type {NewScratchpadData, NewScratchpadTopic} from "./types.ts";

const NewUserScratchpadPage = (): React.ReactElement => {
    // @ts-expect-error - TS2339 - Property 'content_type' does not exist on type '{}'. | TS2339 - Property 'domain' does not exist on type '{}'.
    const {content_type: contentType, domain} = useParams();
    const profileResult = useProfile();

    const scratchpad = React.useMemo(() => {
        const termMap = getTermMap(contentType);
        const newTitle = termMap["new"];

        // sync-start:new-scratchpad 1120175134 services/static/apps/khanacademy/src/scratchpads-package/scratchpad-ui.tsx
        const scratchpad: NewScratchpadData = {
            title: newTitle,
            translatedTitle: newTitle,
            category: null,
            difficulty: null,
            userAuthoredContentType: contentType,
            revision: {
                code: termMap["code"] || "",
                created: new Date(),
            },
        };
        // sync-end:new-scratchpad

        return scratchpad;
    }, [contentType]);
    const topic: NewScratchpadTopic = {
        slug: domain,
        nodeSlug: domain,
        relativeUrl: domain,
        translatedTitle:
            domain === "computer-programming"
                ? t`Computer programming`
                : undefined,
    };
    useUpdatePageSettings({title: scratchpad.translatedTitle});

    const {profile, status: profileStatus} = profileResult;
    const isAIGuideEnabled =
        profile?.get("isAIGuideEnabled") && profileStatus === "success";
    useEffect(() => {
        if (isAIGuideEnabled) {
            GuideStateApi.set("selectedBotName", "UserProgram");
        }
    }, [isAIGuideEnabled]);

    if (profileStatus === "loading") {
        return <RouteLoadSpinner />;
    }

    if (profileStatus === "error" || profileStatus === "aborted") {
        return <ErrorLoadingPage />;
    }

    return (
        <PageRoot template={DefaultWithKhanmigoPageTemplate}>
            <ScratchpadPage
                options={{
                    embedded: false,
                    scratchpad,
                    topic,
                    userScratchpad: null,
                }}
                profile={profile}
                style={styles.bodyWrapper}
            />
        </PageRoot>
    );
};

const styles = StyleSheet.create({
    bodyWrapper: {
        marginLeft: 16,
        marginRight: 16,
    },
});

export default withStrings(NewUserScratchpadPage);
